import React from 'react'
import { motion } from 'framer-motion'
import { useAnimations } from '../contexts/AnimationContext'
import { FigmaCard, FigmaButton } from '../components/FigmaUIElements'
import { Wrench, Hammer, Settings, Zap, Shield, Factory } from 'lucide-react'

const Construction: React.FC = () => {
  const { settings } = useAnimations()

  return (
    <motion.div
      className="min-h-screen p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="text-center mb-8"
          animate={settings.floatingElements ? {
            y: [0, -5, 0],
            transition: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
          } : {}}
        >
          <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent mb-4">
            Construction Center
          </h1>
          <p className="text-gray-300 text-lg">
            Build and upgrade structures across your empire. Expand your infrastructure.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Factory className="w-8 h-8 text-orange-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Production Facilities</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Build factories and refineries to boost resource production.
            </p>
            <FigmaButton variant="primary" className="w-full">
              Build Factory
            </FigmaButton>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Shield className="w-8 h-8 text-blue-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Defense Systems</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Construct planetary shields and defense platforms.
            </p>
            <FigmaButton variant="secondary" className="w-full">
              Build Defenses
            </FigmaButton>
          </FigmaCard>

          <FigmaCard className="p-6">
            <div className="flex items-center mb-4">
              <Zap className="w-8 h-8 text-yellow-400 mr-3" />
              <h3 className="text-xl font-semibold text-white">Power Grid</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Expand energy infrastructure and power generation.
            </p>
            <FigmaButton variant="secondary" className="w-full">
              Upgrade Power
            </FigmaButton>
          </FigmaCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Construction Queue</h3>
            <div className="space-y-4">
              {[
                { project: 'Mining Platform Alpha', progress: 75, eta: '2h 15m' },
                { project: 'Defense Turret Array', progress: 45, eta: '4h 30m' },
                { project: 'Research Laboratory', progress: 20, eta: '8h 45m' }
              ].map((project, index) => (
                <motion.div
                  key={project.project}
                  className="p-4 bg-gray-800/50 rounded-lg"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-medium">{project.project}</h4>
                    <span className="text-gray-400 text-sm">ETA: {project.eta}</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <motion.div
                      className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${project.progress}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                    />
                  </div>
                  <div className="text-right text-sm text-gray-400 mt-1">
                    {project.progress}% Complete
                  </div>
                </motion.div>
              ))}
            </div>
          </FigmaCard>

          <FigmaCard className="p-6">
            <h3 className="text-2xl font-semibold text-white mb-4">Available Blueprints</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: 'Space Dock', cost: '5,000', icon: Factory },
                { name: 'Laser Array', cost: '3,200', icon: Shield },
                { name: 'Solar Plant', cost: '2,800', icon: Zap },
                { name: 'Comm Center', cost: '1,500', icon: Settings }
              ].map((blueprint, index) => (
                <motion.div
                  key={blueprint.name}
                  className="p-3 bg-gray-800/30 rounded-lg text-center cursor-pointer"
                  whileHover={{ scale: 1.05, backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                  whileTap={{ scale: 0.95 }}
                >
                  <blueprint.icon className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <h4 className="text-white text-sm font-medium">{blueprint.name}</h4>
                  <p className="text-gray-400 text-xs">{blueprint.cost} Credits</p>
                </motion.div>
              ))}
            </div>
          </FigmaCard>
        </div>
      </div>
    </motion.div>
  )
}

export default Construction